/**
 * Sidebar navigation, per role.
 *
 * These entries are the live views only. The legacy sidebar also linked to
 * several sections whose handler fell through to a "coming soon" placeholder
 * (`switchDoctorTab`'s default branch) — those are dropped.
 */

import { ROLES } from '../../context/AuthContext';

/**
 * The colour a nav item carries.
 *
 * The legacy admin sidebar tinted every section its own hue — red for doctors,
 * cyan for registrations, purple for packages — and the doctor and patient menus
 * coloured their icons the same way (index.html:804–985). It was the fastest way
 * to find a section without reading it, and the rebuild had flattened all of it
 * to one grey.
 *
 * `tint` colours the whole row: background, border and label. `icontint` colours
 * only the glyph and leaves the label grey until hover.
 */
export const NAV_TINTS = {
  red: {
    text: 'text-red-300',
    icon: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
  },
  cyan: {
    text: 'text-cyan-300',
    icon: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
  },
  purple: {
    text: 'text-purple-300',
    icon: 'text-purple-400',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/20',
  },
  orange: {
    text: 'text-orange-300',
    icon: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/20',
  },
  emerald: {
    text: 'text-emerald-300',
    icon: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
  },
  teal: {
    text: 'text-teal-300',
    icon: 'text-teal-400',
    bg: 'bg-teal-500/10',
    border: 'border-teal-500/20',
  },
  amber: {
    text: 'text-amber-300',
    icon: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
  },
  rose: {
    text: 'text-rose-300',
    icon: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
  },
};

/**
 * The patient tabs.
 *
 * Hoisted out of the patient menu because three roles need them. An admin and
 * a marketing team member are people who also see doctors, and both hold a real
 * patient record — the routes already admitted them (see app/router.jsx), but
 * only one link into the whole section was ever offered, so the rest was
 * reachable by typing a URL and no other way.
 *
 * Settings is deliberately not here: admins and team members already carry
 * their own settings row, and a second one would be two links to one screen.
 */
const PATIENT_TABS = [
  { to: '/patient', end: true, label: 'الرئيسية', icon: 'layout-grid', icontint: 'cyan' },
  { to: '/patient/doctors', label: 'الأطباء', icon: 'stethoscope', icontint: 'cyan' },
  { to: '/patient/appointments', label: 'مواعيدي', icon: 'calendar-clock', icontint: 'cyan' },
  { to: '/patient/prescriptions', label: 'روشتاتي', icon: 'file-text', icontint: 'purple' },
  { to: '/patient/medications', label: 'أدويتي', icon: 'pill', icontint: 'teal' },
  { to: '/patient/medical-cv', label: 'ملفي الطبي', icon: 'heart-pulse', icontint: 'rose' },
  { to: '/patient/transactions', label: 'معاملاتي المالية', icon: 'banknote', icontint: 'emerald' },
];

/** Heading row that splits one role’s menu into sections. Not a link. */
const PATIENT_SECTION = { section: 'حسابي كمريض' };

/** The same, above the hospital screens the upgrade added. */
const HOSPITAL_SECTION = { section: 'إدارة المستشفى' };

/**
 * The seven department desks, as the hospital's management sees them.
 *
 * The owner and the co-admin oversee every desk, and the Super Admin working
 * inside a hospital does too, so the same rows are offered to all three. The
 * labels are the department's name rather than each desk's own menu label,
 * because a manager is choosing *which* department, not doing its job.
 */
export const DEPARTMENTS_SECTION = { section: 'أقسام المستشفى' };
export const DEPARTMENT_ROWS = Object.freeze([
  { to: '/contracts', label: 'التعاقدات والتأمين', icon: 'shield-plus', tint: 'purple' },
  { to: '/accounts', label: 'حسابات المرضى', icon: 'hand-coins', tint: 'emerald' },
  { to: '/rooms', label: 'الغرف والعمليات والعناية', icon: 'bed-double', tint: 'rose' },
  { to: '/nursing', label: 'التمريض', icon: 'syringe', tint: 'teal' },
  { to: '/lab', label: 'المعمل', icon: 'flask-conical', tint: 'cyan' },
  { to: '/radiology', label: 'الأشعة', icon: 'scan-line', tint: 'purple' },
  { to: '/pharmacy', label: 'الصيدلية', icon: 'pill', tint: 'amber' },
]);

/** Every hospital dashboard, for the Super Admin working inside one. */
export const PLATFORM_HOSPITAL_ROWS = Object.freeze([
  { section: 'داخل المستشفى' },
  { to: '/hospital', label: 'فريق المستشفى', icon: 'shield-check', tint: 'purple' },
  { to: '/reception', label: 'مكتب الاستقبال', icon: 'clipboard-list', tint: 'cyan' },
  { to: '/operations', label: 'إدارة العيادات والتأخير', icon: 'layout-dashboard', tint: 'emerald' },
  ...DEPARTMENT_ROWS,
]);

/** The settings row every staff menu ends with. */
const settingsRow = (base) => ({ to: `${base}/settings`, label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' });

export const NAV_ITEMS = {
  [ROLES.PATIENT]: [
    ...PATIENT_TABS,
    { to: '/patient/settings', label: 'الإعدادات والأمان', icon: 'shield-check', icontint: 'amber' },
  ],

  [ROLES.DOCTOR]: [
    { to: '/doctor', end: true, label: 'نظرة عامة', icon: 'layout-dashboard', icontint: 'cyan' },
    // One entry, not two. The calendar lives inside the clinics screen — it is
    // coloured by clinic, and the legacy build kept them together for that
    // reason. /doctor/schedule still resolves here, so old links survive.
    { to: '/doctor/clinics', label: 'العيادات', icon: 'map-pin', icontint: 'emerald' },
    // The one item with a live indicator instead of an icon, matching the
    // legacy sidebar's "عرض Live" button (index.html:952).
    { to: '/doctor/live', label: 'عرض Live', icon: 'monitor-check', tint: 'emerald', pulse: true },
    { to: '/doctor/prescriptions', label: 'أدويتي', icon: 'pill', icontint: 'purple' },

    /*
     * The private-practice rows.
     *
     * `locks` names the capability the row needs. A hospital-affiliated doctor
     * does not hold PERSONAL_CLINIC_MANAGE, so AppShell renders these greyed
     * and non-navigating, and a click opens the upsell instead — which is what
     * the requirement asks for: visible but disabled, with a way to buy the
     * thing they are missing.
     *
     * Naming the capability here rather than listing the paths in the shell
     * keeps the rule in one place: grant that permission to a doctor and every
     * one of these unlocks at once.
     */
    {
      to: '/doctor/offers',
      label: 'العروض والخصومات',
      icon: 'percent',
      icontint: 'orange',
      locks: 'PERSONAL_CLINIC_MANAGE',
    },
    {
      to: '/doctor/vouchers',
      label: 'أكواد الخصم',
      icon: 'ticket',
      icontint: 'amber',
      locks: 'PERSONAL_CLINIC_MANAGE',
    },
    {
      to: '/doctor/packages',
      label: 'باقاتي التسويقية',
      icon: 'package',
      icontint: 'purple',
      locks: 'PERSONAL_CLINIC_MANAGE',
    },
    {
      to: '/doctor/photography',
      label: 'جلسة تصوير',
      icon: 'camera',
      icontint: 'rose',
      locks: 'PERSONAL_CLINIC_MANAGE',
    },
    { to: '/doctor/settings', label: 'الإعدادات والأمان', icon: 'shield-check', icontint: 'amber' },
  ],

  // The admin menu is fully tinted — every section its own colour, which is how
  // the legacy build made eight near-identical rows scannable at a glance.
  [ROLES.ADMIN]: [
    { to: '/admin', end: true, label: 'إدارة الأطباء', icon: 'users', tint: 'red' },
    {
      to: '/admin/registrations',
      label: 'طلبات التسجيل',
      icon: 'user-check',
      tint: 'cyan',
      badge: 'registrations',
    },
    { to: '/admin/packages', label: 'باقات الخدمات', icon: 'package', tint: 'purple' },
    {
      to: '/admin/requests',
      label: 'طلبات الباقات',
      icon: 'shopping-cart',
      tint: 'orange',
      badge: 'packageRequests',
    },
    { to: '/admin/institutions', label: 'المنشآت الطبية', icon: 'building-2', tint: 'teal' },
    { to: '/admin/photography', label: 'فرق التصوير', icon: 'camera', tint: 'rose' },
    { to: '/admin/accounts', label: 'الحسابات', icon: 'wallet', tint: 'emerald' },
    // The AI grant screen. Purple, and the only admin row that is not a
    // business function — it governs a paid capability, so it sits next to the
    // money rather than in settings.
    { to: '/admin/ai', label: 'الذكاء الاصطناعي', icon: 'sparkles', tint: 'purple' },
    { to: '/admin/team', label: 'إدارة الفريق', icon: 'users', tint: 'teal' },
    { to: '/admin/appointments', label: 'الحجوزات', icon: 'calendar-check', tint: 'cyan' },
    { to: '/admin/settings', label: 'الإعدادات والأمان', icon: 'settings', tint: 'amber' },
    PATIENT_SECTION,
    ...PATIENT_TABS,
  ],

  /**
   * A hospital, polyclinic or lab centre manager.
   *
   * Deliberately not the doctor menu with extras bolted on. A manager does
   * not hold a clinic or write prescriptions; they run a catalogue, a roster
   * and a ledger. The one thing they share with a doctor is a calendar, and
   * even that is of service bookings rather than consultations.
   */
  [ROLES.INSTITUTION]: [
    { to: '/institution', end: true, label: 'لوحة المنشأة', icon: 'layout-dashboard', tint: 'cyan' },
    { to: '/institution/services', label: 'الخدمات والأسعار', icon: 'package', tint: 'purple' },
    { to: '/institution/bookings', label: 'حجوزات الخدمات', icon: 'calendar-check', tint: 'emerald' },
    { to: '/institution/members', label: 'الأطباء والموظفون', icon: 'users', tint: 'teal' },
    { to: '/institution/finance', label: 'الحسابات والإيرادات', icon: 'banknote', tint: 'amber' },
    { to: '/institution/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'orange' },

    /*
     * The hospital screens.
     *
     * These were the omission that made the whole upgrade invisible. The router
     * admits ROLES.INSTITUTION to /hospital, /operations and /reception, and the
     * screens were deployed and working — but nothing in this menu pointed at
     * them, so a hospital manager signed in, saw the six rows above, and had no
     * way to reach any of it short of typing the URL.
     *
     * `/hospital` is the important one: it is the only screen that creates the
     * five staff roles, which is the feature the upgrade exists for.
     *
     * The section heading separates them from the rows above because they are a
     * different job — the rows above run the catalogue and the money, these run
     * the building.
     */
    HOSPITAL_SECTION,
    { to: '/hospital', label: 'فريق المستشفى', icon: 'shield-check', tint: 'purple' },
    { to: '/operations', label: 'إدارة العيادات والتأخير', icon: 'layout-dashboard', tint: 'emerald' },
    { to: '/reception', label: 'مكتب الاستقبال', icon: 'clipboard-list', tint: 'cyan' },
    DEPARTMENTS_SECTION,
    ...DEPARTMENT_ROWS,
  ],

  [ROLES.TEAM_MEMBER]: [
    { to: '/team', end: true, label: 'مهامي', icon: 'check-square', icontint: 'cyan' },
    { to: '/team/settings', label: 'الإعدادات والأمان', icon: 'shield-check', icontint: 'amber' },
    PATIENT_SECTION,
    ...PATIENT_TABS,
  ],

  /**
   * The four hospital staff menus.
   *
   * Each is deliberately short. A receptionist with a queue in front of them
   * needs one screen, not a tree — and a menu that offers a row the account
   * will be refused is worse than one that does not, because the refusal
   * arrives after the click.
   *
   * Fully tinted, like the admin menu, because these are operational screens
   * scanned at speed rather than read.
   */
  [ROLES.CO_ADMIN]: [
    { to: '/hospital', end: true, label: 'فريق المنشأة', icon: 'users', tint: 'purple' },
    { to: '/hospital/operations', label: 'إدارة العيادات', icon: 'layout-dashboard', tint: 'emerald' },
    { to: '/hospital/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
    DEPARTMENTS_SECTION,
    ...DEPARTMENT_ROWS,
  ],

  [ROLES.RECEPTIONIST]: [
    { to: '/reception', end: true, label: 'الاستقبال', icon: 'clipboard-list', tint: 'cyan' },
    { to: '/reception/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
  ],

  [ROLES.OPERATIONS_MANAGER]: [
    { to: '/operations', end: true, label: 'إدارة العيادات', icon: 'layout-dashboard', tint: 'emerald' },
    { to: '/operations/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
  ],

  [ROLES.SECRETARY]: [
    // The one row with a live indicator, matching the doctor's "عرض Live".
    { to: '/secretary', end: true, label: 'طابور الكشف', icon: 'monitor-check', tint: 'teal', pulse: true },
    { to: '/secretary/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
  ],

  /*
   * The seven department desks. One screen each — the desk's own dashboard,
   * with its work inside it as tabs — and settings. The people at these desks
   * were not asked whether they wanted software; a menu of one is the kindest
   * menu there is.
   */
  [ROLES.CONTRACTS_MANAGER]: [
    { to: '/contracts', end: true, label: 'التعاقدات والتأمين', icon: 'shield-plus', tint: 'purple' },
    settingsRow('/contracts'),
  ],
  [ROLES.ACCOUNTS_MANAGER]: [
    { to: '/accounts', end: true, label: 'حسابات المرضى', icon: 'hand-coins', tint: 'emerald' },
    settingsRow('/accounts'),
  ],
  [ROLES.ROOMS_MANAGER]: [
    { to: '/rooms', end: true, label: 'الغرف والعمليات والعناية', icon: 'bed-double', tint: 'rose' },
    settingsRow('/rooms'),
  ],
  [ROLES.NURSE]: [
    { to: '/nursing', end: true, label: 'التمريض', icon: 'syringe', tint: 'teal', pulse: true },
    settingsRow('/nursing'),
  ],
  [ROLES.LAB_TECHNICIAN]: [
    { to: '/lab', end: true, label: 'المعمل', icon: 'flask-conical', tint: 'cyan' },
    settingsRow('/lab'),
  ],
  [ROLES.RADIOLOGIST]: [
    { to: '/radiology', end: true, label: 'الأشعة', icon: 'scan-line', tint: 'purple' },
    settingsRow('/radiology'),
  ],
  [ROLES.PHARMACIST]: [
    { to: '/pharmacy', end: true, label: 'الصيدلية', icon: 'pill', tint: 'amber' },
    settingsRow('/pharmacy'),
  ],
};

/** The heading the legacy sidebar printed above each role's section. */
export const NAV_SECTION_LABEL = {
  [ROLES.ADMIN]: 'الإدارة',
  [ROLES.DOCTOR]: 'عيادتي',
  [ROLES.TEAM_MEMBER]: 'فريق العمل',
  [ROLES.INSTITUTION]: 'إدارة المنشأة',
  [ROLES.CO_ADMIN]: 'إدارة المنشأة',
  [ROLES.RECEPTIONIST]: 'الاستقبال',
  [ROLES.OPERATIONS_MANAGER]: 'التشغيل',
  [ROLES.SECRETARY]: 'سكرتارية الطبيب',
  [ROLES.CONTRACTS_MANAGER]: 'التعاقدات',
  [ROLES.ACCOUNTS_MANAGER]: 'الحسابات',
  [ROLES.ROOMS_MANAGER]: 'الغرف',
  [ROLES.NURSE]: 'التمريض',
  [ROLES.LAB_TECHNICIAN]: 'المعمل',
  [ROLES.RADIOLOGIST]: 'الأشعة',
  [ROLES.PHARMACIST]: 'الصيدلية',
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'المدير (Admin)',
  [ROLES.DOCTOR]: 'طبيب (Doctor)',
  [ROLES.PATIENT]: 'مستخدم (User)',
  [ROLES.TEAM_MEMBER]: 'فريق العمل (Team)',
  [ROLES.INSTITUTION]: 'إدارة منشأة (Institution)',
  [ROLES.CO_ADMIN]: 'مدير مساعد (Co-Admin)',
  [ROLES.RECEPTIONIST]: 'استقبال (Reception)',
  [ROLES.OPERATIONS_MANAGER]: 'مدير عيادات (Operations)',
  [ROLES.SECRETARY]: 'سكرتير (Secretary)',
  [ROLES.CONTRACTS_MANAGER]: 'التعاقدات والتأمين (Contracts)',
  [ROLES.ACCOUNTS_MANAGER]: 'الحسابات (Accounts)',
  [ROLES.ROOMS_MANAGER]: 'الغرف والعمليات (Rooms)',
  [ROLES.NURSE]: 'تمريض (Nursing)',
  [ROLES.LAB_TECHNICIAN]: 'المعمل (Lab)',
  [ROLES.RADIOLOGIST]: 'الأشعة (Radiology)',
  [ROLES.PHARMACIST]: 'الصيدلية (Pharmacy)',
};

export const navItemsFor = (role) => NAV_ITEMS[role] || [];

/**
 * A stable React key for a menu row.
 *
 * Section headings carry no `to`, and keying every row by `item.to` gave them
 * all the same undefined key.
 */
export const navKey = (item) => item.to || `section:${item.section}`;
